import { api } from './api';
import type {
  AiModel,
  AiModelType,
  AiModelProviderType,
  AiModelCapabilities,
  AiModelPricing,
  AiModelTraining,
  AiProviderSpec,
  AiModelTestResult,
  AiModelEmbedResult,
  AiDocumentAnalysisResult,
  AiConnectionTestResult,
  AiModelListResult,
  AiModelProviderType as ProviderTypeForTest,
  PaginatedAiModelResponse,
} from '../types';

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// Request types aligned to BE ai-model schema.
// Required: providerType, modelId, displayName.
export interface AiModelCreateRequest {
  providerType: AiModelProviderType;
  modelId: string;
  displayName: string;
  version?: string | null;
  description?: string | null;
  iconPath?: string | null;
  modelType?: AiModelType | null;
  modality?: string[];
  // Optional at catalog time — a model may be registered before its credentials
  // exist. When set, the BE refuses the save unless the connection supplies
  // everything the provider needs.
  connectionId?: string | null;
  capabilities?: AiModelCapabilities | null;
  paramsSchema?: Record<string, any> | null;
  pricing?: AiModelPricing | null;
  training?: AiModelTraining | null;
  isDefault?: boolean;
  isActive?: boolean;
  isDeprecated?: boolean;
  retiresAt?: string | null;
  appId?: string;   // Create-only; owning project (honoured by BE only for callers who can access it)
}

// Update payload mirrors create minus appId (owning project changed via /{id}/project).
export type AiModelUpdateRequest = Omit<AiModelCreateRequest, 'appId'>;

// BE /ai-models/list query params (snake_case, matches FastAPI Query names).
export interface AiModelListParams {
  page?: number;
  page_size?: number;
  search?: string;
  include_deleted?: boolean;
  provider_type?: string;
  model_type?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string; // JSON per-column filters
  app_id?: string;  // Filter to one accessible project (intersected server-side)
  /**
   * Only models a prompt can actually be sent to. Excludes embedding and
   * document-analysis models,
   * which answer a different endpoint and cannot be dispatched by this
   * platform's single complete() contract. Use for any model PICKER; leave off
   * when listing the registry itself.
   */
  runnable_only?: boolean;
  /** Only models whose modality includes text (no speech, transcription or detection-only models). */
  text_only?: boolean;
  /** Only models that are active and configured — the ones a run can actually use. */
  ready_only?: boolean;
  include_global?: boolean; // also include core/global rows (Show core toggle)
}

/** Re-scope preview: which projects are aligned to an AI model. */
export interface RescopePreview {
  canRescope: boolean;
  conflicts: Record<string, Record<string, number>>;
  references: Record<string, Record<string, number>>;
}

export const aiModelsService = {
  // List all AI models with pagination + optional search/filters
  list: async (params?: AiModelListParams): Promise<PaginatedAiModelResponse> => {
    const response = await api.get<PaginatedAiModelResponse>('/api/v1/ai-models/list', { params });
    return response.data;
  },

  /**
   * What each provider needs to authenticate.
   *
   * Fetched rather than hardcoded: the form renders the fields a provider
   * actually takes — an Azure endpoint and api-version, a Bedrock region, a
   * Vertex project and location — instead of the single token box that only
   * ever fitted OpenAI. Cache it; it is identical for every caller and changes
   * only with a backend release.
   */
  getProviderSpecs: async (): Promise<AiProviderSpec[]> => {
    const response = await api.get<ApiResponse<AiProviderSpec[]>>('/api/v1/ai-models/provider-specs');
    return response.data.data;
  },

  /**
   * Call the model once to prove the credentials work.
   *
   * Resolves with ok:false rather than throwing when the provider rejects the
   * request — a failed test is a result to show, not an error to catch.
   */
  test: async (modelId: string): Promise<AiModelTestResult> => {
    const response = await api.post<ApiResponse<AiModelTestResult>>(`/api/v1/ai-models/${modelId}/test`);
    return response.data.data;
  },

  /**
   * Turn texts into vectors with a model registered as modelType 'embedding'.
   * Vectors come back in the order the texts were sent. Pass 'default' as the
   * id to use the project's default embedding model.
   */
  embed: async (modelId: string, texts: string[]): Promise<AiModelEmbedResult> => {
    const response = await api.post<ApiResponse<AiModelEmbedResult>>(
      `/api/v1/ai-models/${modelId}/embed`, { texts },
    );
    return response.data.data;
  },

  /**
   * Extract a document's text with a model registered as modelType
   * 'document_analysis' (Azure Document Intelligence). Pass 'default' as the id
   * to use the project's default document model. Nothing is stored — the text
   * comes back to hand to an LLM.
   */
  analyzeDocument: async (
    modelId: string,
    file: File,
    options?: { pages?: string; locale?: string; outputFormat?: 'markdown' | 'text'; includePages?: boolean },
  ): Promise<AiDocumentAnalysisResult> => {
    const form = new FormData();
    form.append('file', file);
    if (options?.pages) form.append('pages', options.pages);
    if (options?.locale) form.append('locale', options.locale);
    if (options?.outputFormat) form.append('output_format', options.outputFormat);
    if (options?.includePages !== undefined) form.append('include_pages', String(options.includePages));
    const response = await api.post<ApiResponse<AiDocumentAnalysisResult>>(
      `/api/v1/ai-models/${modelId}/analyze-document`, form,
      // Overrides the instance's application/json default, which would make
      // axios serialise the FormData as JSON. Same as file-upload.service.
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data.data;
  },

  /**
   * Call the provider with credentials that are not saved yet.
   *
   * Answers "does this key work" before a connection and a model row are
   * written. Resolves with ok:false when the provider rejects the call — that
   * is the answer being asked for, not an error to catch.
   */
  testConnection: async (input: {
    providerType: ProviderTypeForTest;
    modelId: string;
    modelType?: string | null;
    connectionId?: string | null;
    apiKey?: string | null;
    connectionConfig?: Record<string, string>;
  }): Promise<AiConnectionTestResult> => {
    const response = await api.post<ApiResponse<AiConnectionTestResult>>(
      '/api/v1/ai-models/test-connection', input,
    );
    return response.data.data;
  },

  /**
   * Ask the provider which models this credential can see, for the model picker.
   * Same input as testConnection minus the model, which is what is being
   * looked for. Free: nothing is sent to a model.
   */
  listAvailable: async (input: {
    providerType: ProviderTypeForTest;
    connectionId?: string | null;
    apiKey?: string | null;
    connectionConfig?: Record<string, string>;
  }): Promise<AiModelListResult> => {
    const response = await api.post<ApiResponse<AiModelListResult>>(
      '/api/v1/ai-models/list-available', input,
    );
    return response.data.data;
  },

  // Get AI model by ID
  getById: async (modelId: string): Promise<AiModel> => {
    const response = await api.get<ApiResponse<AiModel>>(`/api/v1/ai-models/get/${modelId}`);
    return response.data.data;
  },

  // Conflict preview before a re-scope. There is no BE preview route for AI models;
  // the BE enforces (appId, modelCode) uniqueness on apply (409), so the preview is
  // advisory and permissive here — mirrors eval-suites.getRescopePreview.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getRescopePreview: async (_id?: string, _appId?: string | null): Promise<RescopePreview> => ({
    canRescope: true, conflicts: {}, references: {},
  }),

  // Change an AI model's owning project (null/empty = global). Platform admin only.
  rescopeProject: async (id: string, appId: string | null): Promise<AiModel> => {
    const response = await api.patch<{ data: AiModel }>(
      `/api/v1/ai-models/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  // Create new AI model
  create: async (data: AiModelCreateRequest): Promise<AiModel> => {
    const response = await api.post<ApiResponse<AiModel>>('/api/v1/ai-models/create', data);
    return response.data.data;
  },

  // Update AI model (BE uses PUT)
  update: async (modelId: string, data: AiModelUpdateRequest): Promise<AiModel> => {
    const response = await api.put<ApiResponse<AiModel>>(`/api/v1/ai-models/update/${modelId}`, data);
    return response.data.data;
  },

  // Soft delete AI model
  softDelete: async (modelId: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/ai-models/delete/${modelId}`);
    return response.data.data;
  },

  // Restore a soft-deleted AI model
  restore: async (modelId: string): Promise<{ message: string }> => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/api/v1/ai-models/restore/${modelId}`);
    return response.data.data;
  },
};
